export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Welding Fundamentals, Safety, Equipment & Metal Preparation',
    subtitle: 'The Foundation Every Welding Student Needs',
    intro: [
      'Every new group starts with the same challenge: the welding circuit, power sources, PPE, fumes, fire rules, gas cylinders and metal prep, all before the first real bead.',
      'The basics look simple, but students only get them right when they can see every part, every hazard and every common mistake.',
      'This volume turns welding fundamentals into labeled diagrams, comparison tables, step-by-step sequences, ❌/✅ comparisons, teaching cues and shop drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'How the electric arc works',
      'Welding electricity basics: AC, DCEP & DCEN',
      'CC vs. CV power sources & duty cycle',
      'Welding machine front panel',
      'PPE head to toe',
      'Welding helmets & lens shades',
      'Fume & ventilation control',
      'Fire prevention & hot-work rules',
      'Compressed gas cylinder safety',
      'Metal properties, carbon steels, stainless & aluminum',
      'Heat, expansion & distortion control',
      'The 5 basic joint types & parts of a weld',
      'Welding positions overview',
      'Cleaning, grinding, cutting & beveling',
      'Fit-up & tack welding',
      'Safety orientation day & 4-week fundamentals plan',
    ],
    result: 'Give every student a safe, confident start with the machine, the PPE and the metal before their first weld.',
  },
  {
    number: 'Volume 02',
    title: 'MIG/MAG Welding, GMAW Techniques & Practical Applications',
    subtitle: 'Set It Up, Tune the Arc, Run Sound Beads',
    intro: [
      'MIG is where most students strike their first real arc, and where settings, wire feed and gun angles cause the most confusion.',
      'Without a clear method, small setup mistakes turn into porosity, cold lap or burn-through.',
      'This volume organizes GMAW into component diagrams, starting settings charts, transfer-mode comparisons, gun-angle visuals, defect cards and booth exercises.',
    ],
    popular: true,
    popularLabel: 'CORE PROCESS',
    bulletsIntro: 'Inside:',
    bullets: [
      'GMAW system components & the MIG gun',
      'Wire feeder, drive rolls & threading the wire',
      'Polarity for wire welding',
      'Shielding gases & setting gas flow',
      'Wire classifications & diameters',
      'Metal transfer modes: short-circuit & spray',
      'Voltage & wire feed speed relationship',
      'Starting settings chart for carbon steel',
      'Contact-tip-to-work distance (stick-out)',
      'Gun angles: push vs. drag',
      'Travel speed & bead width',
      'Positions: 1F/1G, 2F, 3F/3G & 4F/4G',
      'Flux-cored arc welding (FCAW)',
      'Porosity, cold lap, undercut & burn-through',
      'Bird-nesting & consumable maintenance',
      '5 practice exercises & 4-week MIG plan',
    ],
    result: 'Help students set up the machine, tune the arc and run consistent, sound MIG welds in every position.',
  },
  {
    number: 'Volume 03',
    title: 'TIG Welding, GTAW Techniques & Precision Fabrication',
    subtitle: 'Control, Cleanliness and Precision',
    intro: [
      'TIG demands coordination: tungsten, torch angle, filler rod and amperage control all at the same time.',
      'Explaining tungsten prep, AC balance or dabbing technique is hard until students can see every step side by side.',
      'This volume covers GTAW with exploded torch diagrams, tungsten charts, torch and filler angle visuals, ❌/✅ cards, troubleshooting tables and puddle-control drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'TIG system components & the torch exploded',
      'Polarity, current, AC balance & frequency',
      'Arc starting methods',
      'Tungsten types, color codes & sizes',
      'Grinding tungsten correctly',
      'Stick-out, cup selection & gas lens',
      'Shielding gas for TIG',
      'Filler metals & rod diameter selection',
      'Foot pedal & fingertip amperage control',
      'Torch & filler rod angles',
      'Dabbing, laying the wire & walking the cup',
      'Stainless steel, heat tint & back purging',
      'Aluminum TIG & puddle control',
      'Thin sheet, pulse TIG & small tube',
      'Tungsten inclusions, porosity & sugaring',
      'TIG troubleshooting table & 4-week plan',
    ],
    result: 'Help students build the control and cleanliness that precise, high-quality TIG welds require.',
  },
  {
    number: 'Volume 04',
    title: 'Stick Welding, SMAW Processes & Structural Welding',
    subtitle: 'Strike It, Control It, Build It Strong',
    intro: [
      'Stick welding is where students learn arc control the hard way: sticking electrodes, slag inclusions and uneven beads.',
      'Electrode selection, arc length, angles and multipass sequences are hard to teach without clear visuals and exact ranges.',
      'This volume explains SMAW and structural welding with electrode charts, angle diagrams, position sequences, ❌/✅ comparisons and practice exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'SMAW equipment setup',
      'Reading an electrode classification',
      'The four electrodes every student must know',
      'Low-hydrogen electrode storage',
      'Electrode diameter & amperage ranges',
      'Polarity in SMAW',
      'Striking the arc & arc length control',
      'Electrode angles & travel speed',
      'Weave motions, restarts & tie-ins',
      'Slag removal & interpass cleaning',
      'Fillets in 1F, 2F, 3F & 4F',
      'Groove welds on plate (1G–4G)',
      'Open root technique with E6010',
      'Multipass welds & structural joints',
      'Introduction to AWS D1.1 & reading a WPS',
      'Slag inclusions, porosity, undercut & cracks',
    ],
    result: 'Help students strike, control and build strong multipass stick welds ready for structural work.',
  },
  {
    number: 'Volume 05',
    title: 'Welding Blueprints, Symbols, Inspection & Quality Control',
    subtitle: 'Read the Print, Weld to Spec, Inspect the Result',
    intro: [
      'A welder who cannot read a print or a welding symbol cannot work to spec, and inspection is where many students feel lost.',
      'Arrow side vs. other side, groove dimensions, WPS documents and discontinuities are hard to teach with words alone.',
      'This volume explains blueprint reading, AWS A2.4 welding symbols, inspection and quality control with annotated drawings, symbol breakdowns, inspection checklists and practice exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Parts of a fabrication drawing & lines',
      'Orthographic, isometric & section views',
      'Dimensions, tolerances & cut lists',
      'The welding symbol: anatomy',
      'Arrow side vs. other side',
      'Fillet & intermittent fillet symbols',
      'Groove weld symbols & dimensions',
      'Supplementary symbols & tail information',
      'ISO 2553 vs. AWS A2.4',
      'WPS, PQR & WPQ',
      'Inspection before, during & after welding',
      'Visual inspection tools & measuring fillets',
      'Discontinuity vs. defect',
      'NDT overview: PT, MT, UT & RT',
      'Destructive testing & bend test coupons',
      'Writing an inspection report',
    ],
    result: 'Help students read prints, weld to the symbol and inspect their own work with clear quality criteria.',
  },
  {
    number: 'Volume 06',
    title: 'Welding Instruction, Workshop Training & Practical Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to weld is not the same as knowing how to teach it.',
      'Welding instructors need clear methods for lessons, demonstrations, booth coaching, shop safety, mixed-level groups, practice projects and practical assessment.',
      'This volume gives you lesson plans, demonstration scripts, cue libraries, safety checklists, shop projects, course plans, rubrics and certificates ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The role of the welding instructor',
      'How adults learn practical skills',
      'Designing a welding program & learning objectives',
      'Lesson plan template & sample lesson plans',
      'The pre-lab safety briefing',
      'Running an effective demonstration',
      'Coaching at the booth & instructor cue library',
      'Giving feedback on welds',
      'Skill progression ladder & coupon standards',
      'Daily safety & weekly equipment checklists',
      'Hot-work & fire watch procedure for classes',
      'Workshop activities & student practice projects',
      'Competency-based assessment & rubrics',
      'Preparing students for certification tests',
      'Teaching mixed-level groups',
      '12-week welding course plan & certificates',
    ],
    result: 'Plan, deliver and assess every lesson, workshop and course with structure, from first-time beginners to advanced students.',
  },
]
